/**
 * FindStore Component
 *
 * This component displays a list of stores with their names and addresses.
 * Users can select a store using radio buttons, which updates the
 * state to reflect the selected store. The selected store can then be
 * used for further actions, such as displaying additional information
 * or making a reservation.
 *
 * Features:
 * - Renders a scrollable list of stores on small screens.
 * - Utilizes radio buttons to allow users to select a store.
 * - Updates the UI to highlight the selected store.
 * - Displays an iframe (for tracking or analytics) on the right side.
 *
 * Store Data:
 * - The store information is defined in a static array with IDs, names,
 *   and addresses.
 *
 *
 **/
import React, { useEffect, useState } from 'react';
import { store } from '../../../../../types/product/store';
import { props } from './NeighborhoodDiscovery';
import { useSearchParams } from 'next/navigation';

// Haversine Formula to calculate the distance between two points (in miles)
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const R = 3958.8; // Radius of the Earth in miles
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const FindStore = ({
  storeData,
  selectedPlace,
  handleSelectedPlace,
  userLocation,
}: Omit<props, 'setIsOpen' | 'isOpen' | 'setUserLocation'>) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const searchParams = useSearchParams();
  const query = searchParams.get('q');
  // Reorder storeData so that selectedPlace appears first in the list
  const selectedStore = storeData?.filter((store: store) => store?.name?.['en-GB'] === selectedPlace?.name);
  const sortedStoreData = selectedPlace
    ? [selectedStore?.[0], ...storeData.filter((store: store) => store.name['en-GB'] !== selectedPlace?.name)]
    : storeData;

  return (
    <>
      {isClient && selectedPlace !== null && (
        <div className="p-15 md:p-30">
          <div className="flex-col md:flex-row">
            <div className="w-auto pt-12 md:pt-0">
              <div className="flex-col gap-10">
                <div className="max-h-[500px] overflow-y-auto md:overflow-y-auto">
                  {/* Show scrollbar on md and up */}
                  {sortedStoreData?.map((store: store) => {
                    const distance =
                      userLocation && store?.geoLocation?.coordinates?.[1] && store?.geoLocation?.coordinates?.[0]
                        ? calculateDistance(
                            userLocation.lat,
                            userLocation.lng,
                            store?.geoLocation?.coordinates?.[1],
                            store?.geoLocation?.coordinates?.[0],
                          )
                        : null;
                    return (
                      <div className="flex items-center border-b py-15" key={store?.id}>
                        <label className="relative inline-flex cursor-pointer flex-col items-start" htmlFor={store?.id}>
                          <input
                            name="store-location"
                            type="radio"
                            className="h-20 w-20 cursor-pointer appearance-none rounded-full transition-all"
                            id={store?.id}
                            checked={selectedPlace?.name === store?.name?.['en-GB'] || query === store?.name?.['en-GB']}
                            onChange={() =>
                              handleSelectedPlace({
                                name: store?.name['en-GB'],
                                location: {
                                  lat: store?.geoLocation?.coordinates[1],
                                  lng: store?.geoLocation?.coordinates[0],
                                },
                              })
                            }
                          />
                          <span className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-black opacity-0 transition-opacity duration-200 checked:opacity-100"></span>
                        </label>
                        <label className="ml-10 flex cursor-pointer items-start" htmlFor={store?.id}>
                          <div>
                            <p
                              key={store?.id}
                              className={`pb-4 text-16 font-medium leading-5 ${
                                selectedPlace?.name === store?.name['en-GB'] ? 'text-[#127E57]' : 'text-[#4B4B4B]'
                              }`}
                            >
                              {store?.name?.['en-GB']}
                              {distance ? (
                                <span className="ml-4 text-sm text-[#127e57]">{distance.toFixed(2)} miles</span>
                              ) : null}
                            </p>
                            <div className="text-14 font-normal text-[#4B4B4B]">
                              <p>{store?.address?.streetNumber}</p>
                              <p>{store?.address?.streetName}</p>
                              <p>{store?.address?.postalCode}</p>
                              <p>{store?.address?.city}</p>
                              <p>{store?.address?.state}</p>
                              <p>{store?.address?.country}</p>
                              <p>{store?.address?.phone}</p>
                              <p>{store?.address?.email}</p>
                            </div>
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!selectedStore && (
        <div className="ml-10 mt-40 w-[70%]">We are sorry we could not find results for your search</div>
      )}
    </>
  );
};

export default FindStore;
